import { useEffect, useRef } from 'react';
import { usePoints } from '../contexts/PointsContext';

const PARTICLE_COUNT = 8;
const TRAIL_COUNT = 6;

const FloatingPoints: React.FC = () => {
  const { animations, removeAnimation, targetRef } = usePoints();

  return (
    <>
      {animations.map(anim => (
        <FloatingPointItem
          key={anim.id}
          id={anim.id}
          amount={anim.amount}
          startX={anim.startX}
          startY={anim.startY}
          targetRef={targetRef}
          onDone={removeAnimation}
        />
      ))}
    </>
  );
};

interface FloatingPointItemProps {
  id: number;
  amount: number;
  startX: number;
  startY: number;
  targetRef: React.RefObject<HTMLDivElement | null>;
  onDone: (id: number) => void;
}

const FloatingPointItem: React.FC<FloatingPointItemProps> = ({
  id, amount, startX, startY, targetRef, onDone
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const target = targetRef.current;
    if (!container || !target) {
      onDone(id);
      return;
    }

    const targetRect = target.getBoundingClientRect();
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;
    const dx = endX - startX;
    const dy = endY - startY;

    container.style.setProperty('--fly-dx', `${dx}px`);
    container.style.setProperty('--fly-dy', `${dy}px`);

    // Burst particles at origin
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const angle = (Math.PI * 2 * i) / PARTICLE_COUNT + (Math.random() - 0.5) * 0.4;
      const dist = 40 + Math.random() * 50;
      const particle = document.createElement('div');
      particle.className = 'fp-burst-particle';
      particle.style.setProperty('--burst-x', `${Math.cos(angle) * dist}px`);
      particle.style.setProperty('--burst-y', `${Math.sin(angle) * dist}px`);
      particle.style.setProperty('--burst-size', `${3 + Math.random() * 5}px`);
      particle.style.setProperty('--burst-delay', `${Math.random() * 0.1}s`);
      container.appendChild(particle);
    }

    // Trail particles along the path
    for (let i = 0; i < TRAIL_COUNT; i++) {
      const trail = document.createElement('div');
      trail.className = 'fp-trail-particle';
      trail.style.setProperty('--trail-dx', `${dx}px`);
      trail.style.setProperty('--trail-dy', `${dy}px`);
      trail.style.setProperty('--trail-delay', `${0.08 + i * 0.06}s`);
      trail.style.setProperty('--trail-spread', `${(Math.random() - 0.5) * 20}px`);
      container.appendChild(trail);
    }

    // Start the main animation
    requestAnimationFrame(() => {
      container.classList.add('fp-animate');
    });

    const timer = setTimeout(() => onDone(id), 1200);
    return () => clearTimeout(timer);
  }, [id, startX, startY, targetRef, onDone]);

  return (
    <div
      ref={containerRef}
      className="fp-container"
      style={{ left: startX, top: startY }}
    >
      <div className="fp-glow" />
      <div className="fp-star">
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
        </svg>
      </div>
      <div className="fp-text">+{amount}</div>
    </div>
  );
};

export default FloatingPoints;
